// Per-user activity feeds (org admin side): prints, AI posters, purchases.
// Designs join in Phase 2 once library sync ships.

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { orgApi } from '../../services/orgApi';
import { formatDate } from '../../i18n/formatters';
import { orgStyles } from './OrgConsole';

const FEEDS = [
  { key: 'prints', labelKey: 'activity.feeds.prints' },
  { key: 'posters', labelKey: 'activity.feeds.posters' },
  { key: 'purchases', labelKey: 'activity.feeds.purchases' },
];

function fmtWhen(iso) {
  if (!iso) return '';
  return formatDate(iso);
}

export default function ActivityTab() {
  const { t } = useTranslation('orgAdmin');
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token;

  const [roster, setRoster] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [feed, setFeed] = useState('prints');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Roster for the user picker (reuses /org/users).
  useEffect(() => {
    if (!token) return;
    orgApi.listUsers(token)
      .then((data) => {
        const all = [...(data?.users || []), ...(data?.unassigned || [])];
        setRoster(all);
        if (all.length > 0) setSelectedUserId((prev) => prev || all[0].id);
      })
      .catch((e) => toast.showError(t('activity.toasts.loadUsersFailed', { error: e.message })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const refresh = useCallback(async () => {
    if (!token || !selectedUserId) return;
    setLoading(true);
    try {
      const data = await orgApi.userActivity(token, selectedUserId, feed);
      setItems(data?.items || []);
    } catch (e) {
      toast.showError(t('activity.toasts.loadActivityFailed', { error: e.message }));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, selectedUserId, feed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const selectStyle = {
    padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
    background: '#FFFFFF', border: '1px solid rgba(27,37,80,0.142)',
    color: 'rgba(27,37,80,0.855)', outline: 'none',
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} style={selectStyle}>
          {roster.map((u) => (
            <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '6px' }}>
          {FEEDS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFeed(f.key)}
              style={{
                ...orgStyles.btn,
                padding: '6px 12px',
                fontSize: '12px',
                background: feed === f.key ? 'rgba(127,75,246,0.25)' : 'rgba(27,37,80,0.048)',
                borderColor: feed === f.key ? 'rgba(127,75,246,0.6)' : 'rgba(27,37,80,0.114)',
                color: feed === f.key ? '#5F2FD1' : 'rgba(27,37,80,0.617)',
              }}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={{ ...orgStyles.muted, padding: '24px 4px' }}>{t('loading')}</div>}
      {!loading && items.length === 0 && (
        <div style={{ ...orgStyles.card, ...orgStyles.muted, textAlign: 'center', padding: '36px' }}>
          {t('activity.empty')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
        {items.map((item, i) => {
          const img = item.imageUrl || item.thumbnailUrl || null;
          let title; let sub;
          if (feed === 'prints') {
            title = `${item.printer_model || t('activity.item.printFallback')}${item.paper_type ? ` · ${item.paper_type}` : ''}`;
            sub = [
              item.print_width_inches && item.print_height_inches
                ? `${item.print_width_inches}" × ${item.print_height_inches}"` : null,
              item.ink_estimate_cost_usd != null ? t('activity.item.inkCost', { amount: Number(item.ink_estimate_cost_usd).toFixed(2) }) : null,
              fmtWhen(item.created_at),
            ].filter(Boolean).join(' · ');
          } else if (feed === 'posters') {
            title = item.title || t('activity.item.posterFallback');
            sub = [item.styleType, item.status, fmtWhen(item.createdAt)].filter(Boolean).join(' · ');
          } else {
            title = item.title || t('activity.item.printableFallback');
            sub = [
              item.pricePaid != null ? t('activity.item.creditsPaid', { count: item.pricePaid }) : null,
              fmtWhen(item.purchasedAt),
            ].filter(Boolean).join(' · ');
          }
          return (
            <div key={item.id || item.entitlementId || i} style={{ ...orgStyles.card, display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 12px' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '6px', flexShrink: 0,
                background: 'rgba(27,37,80,0.057)', overflow: 'hidden',
              }}>
                {img && <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...orgStyles.text, fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {title}
                </div>
                <div style={{ ...orgStyles.muted, fontSize: '11px', marginTop: '2px' }}>{sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
